import { fetchAuthSession, fetchDevices, forgetDevice } from 'aws-amplify/auth'

type AuthDevice = Awaited<ReturnType<typeof fetchDevices>>[number]
import React from 'react'
import { ThunkAction, ThunkDispatch } from 'redux-thunk'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import { RemovedCognitoDeviceMessage } from 'src/app/Toast/Toast'
import { RootState } from 'src/redux/createRootReducer'

export const GET_COGNITO_TRUSTED_DEVICES_INIT =
  'auth/GET_COGNITO_TRUSTED_DEVICES_INIT'
export const GET_COGNITO_TRUSTED_DEVICES_SUCCESS =
  'auth/GET_COGNITO_TRUSTED_DEVICES_SUCCESS'
export const GET_COGNITO_TRUSTED_DEVICES_FAILURE =
  'auth/GET_COGNITO_TRUSTED_DEVICES_FAILURE'

export interface GetCognitoTrustedDevicesInitAction {
  type: typeof GET_COGNITO_TRUSTED_DEVICES_INIT
}

export interface GetCognitoTrustedDevicesSuccessAction {
  type: typeof GET_COGNITO_TRUSTED_DEVICES_SUCCESS
  payload: AuthDevice[]
}

export interface GetCognitoTrustedDevicesFailureAction {
  type: typeof GET_COGNITO_TRUSTED_DEVICES_FAILURE
  payload: string
}

export type GetCognitoTrustedDevicesActions =
  | GetCognitoTrustedDevicesInitAction
  | GetCognitoTrustedDevicesSuccessAction
  | GetCognitoTrustedDevicesFailureAction

const createGetCognitoTrustedDevicesInitAction =
  (): GetCognitoTrustedDevicesInitAction => ({
    type: GET_COGNITO_TRUSTED_DEVICES_INIT,
  })

const createGetCognitoTrustedDevicesSuccessAction = (
  payload: AuthDevice[],
): GetCognitoTrustedDevicesSuccessAction => ({
  type: GET_COGNITO_TRUSTED_DEVICES_SUCCESS,
  payload: payload,
})

const createGetCognitoTrustedDevicesFailureAction = (
  payload: string,
): GetCognitoTrustedDevicesFailureAction => ({
  type: GET_COGNITO_TRUSTED_DEVICES_FAILURE,
  payload: payload,
})

export const getCognitoTrustedDevices = (): ThunkAction<
  Promise<void>,
  RootState,
  void,
  GetCognitoTrustedDevicesActions
> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, GetCognitoTrustedDevicesActions>,
  ): Promise<void> => {
    dispatch(createGetCognitoTrustedDevicesInitAction())
    try {
      const devices = await fetchDevices()
      dispatch(createGetCognitoTrustedDevicesSuccessAction(devices))
    } catch (error) {
      if (typeof error === 'string') {
        dispatch(
          createGetCognitoTrustedDevicesFailureAction(error.toUpperCase()),
        )
      } else if (error instanceof Error) {
        dispatch(createGetCognitoTrustedDevicesFailureAction(error.message))
      }
    }
  }
}

export const GET_CURRENT_DEVICE_KEY_INIT = 'auth/GET_CURRENT_DEVICE_KEY_INIT'
export const GET_CURRENT_DEVICE_KEY_SUCCESS =
  'auth/GET_CURRENT_DEVICE_KEY_SUCCESS'
export const GET_CURRENT_DEVICE_KEY_FAILURE =
  'auth/GET_CURRENT_DEVICE_KEY_FAILURE'

export interface GetCurrentDeviceKeyInitAction {
  type: typeof GET_CURRENT_DEVICE_KEY_INIT
}

export interface GetCurrentDeviceKeySuccessAction {
  type: typeof GET_CURRENT_DEVICE_KEY_SUCCESS
  payload: string
}

export interface GetCurrentDeviceKeyFailureAction {
  type: typeof GET_CURRENT_DEVICE_KEY_FAILURE
  payload: string
}

export type GetCurrentDeviceKeyActions =
  | GetCurrentDeviceKeyInitAction
  | GetCurrentDeviceKeySuccessAction
  | GetCurrentDeviceKeyFailureAction

const createGetCurrentDeviceKeyInitAction =
  (): GetCurrentDeviceKeyInitAction => ({
    type: GET_CURRENT_DEVICE_KEY_INIT,
  })

const createGetCurrentDeviceKeySuccessAction = (
  payload: string,
): GetCurrentDeviceKeySuccessAction => ({
  type: GET_CURRENT_DEVICE_KEY_SUCCESS,
  payload: payload,
})

const createGetCurrentDeviceKeyFailureAction = (
  payload: string,
): GetCurrentDeviceKeyFailureAction => ({
  type: GET_CURRENT_DEVICE_KEY_FAILURE,
  payload: payload,
})

export const getCurrentDeviceKey = (): ThunkAction<
  Promise<void>,
  RootState,
  void,
  GetCurrentDeviceKeyActions
> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, GetCurrentDeviceKeyActions>,
  ): Promise<void> => {
    dispatch(createGetCurrentDeviceKeyInitAction())
    try {
      const { tokens } = await fetchAuthSession()
      const deviceKey = tokens?.accessToken?.payload?.device_key?.toString()
      // would we ever actually get here without an authed session?
      if (deviceKey === undefined) return
      dispatch(createGetCurrentDeviceKeySuccessAction(deviceKey))
    } catch (error) {
      if (typeof error === 'string') {
        dispatch(createGetCurrentDeviceKeyFailureAction(error.toUpperCase()))
      } else if (error instanceof Error) {
        dispatch(createGetCurrentDeviceKeyFailureAction(error.message))
      }
    }
  }
}

export const FORGET_COGNITO_DEVICE_INIT = 'auth/FORGET_COGNITO_DEVICE_INIT'
export const FORGET_COGNITO_DEVICE_SUCCESS =
  'auth/FORGET_COGNITO_DEVICE_SUCCESS'
export const FORGET_COGNITO_DEVICE_FAILURE =
  'auth/FORGET_COGNITO_DEVICE_FAILURE'

export interface ForgetCognitoDeviceInitAction {
  type: typeof FORGET_COGNITO_DEVICE_INIT
}

export interface ForgetCognitoDeviceSuccessAction {
  type: typeof FORGET_COGNITO_DEVICE_SUCCESS
  payload: string
}

export interface ForgetCognitoDeviceFailureAction {
  type: typeof FORGET_COGNITO_DEVICE_FAILURE
  payload: string
}

export type ForgetCognitoDeviceActions =
  | ForgetCognitoDeviceInitAction
  | ForgetCognitoDeviceSuccessAction
  | ForgetCognitoDeviceFailureAction

const createForgetCognitoDeviceInitAction =
  (): ForgetCognitoDeviceInitAction => ({
    type: FORGET_COGNITO_DEVICE_INIT,
  })

const createForgetCognitoDeviceSuccessAction = (
  payload: string,
): ForgetCognitoDeviceSuccessAction => ({
  type: FORGET_COGNITO_DEVICE_SUCCESS,
  payload: payload,
})

const createForgetCognitoDeviceFailureAction = (
  payload: string,
): ForgetCognitoDeviceFailureAction => ({
  type: FORGET_COGNITO_DEVICE_FAILURE,
  payload: payload,
})

export const forgetCognitoDevice = (
  device: AuthDevice,
): ThunkAction<Promise<void>, RootState, void, ForgetCognitoDeviceActions> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, ForgetCognitoDeviceActions>,
  ): Promise<void> => {
    dispatch(createForgetCognitoDeviceInitAction())
    try {
      await forgetDevice({ device })
      dispatch(createForgetCognitoDeviceSuccessAction(device.id))
      dispatch(
        createSetIsToastOpen(true, {
          toastMessage: <RemovedCognitoDeviceMessage />,
        }),
      )
    } catch (error) {
      if (typeof error === 'string') {
        dispatch(createForgetCognitoDeviceFailureAction(error.toUpperCase()))
      } else if (error instanceof Error) {
        dispatch(createForgetCognitoDeviceFailureAction(error.message))
      }
    }
  }
}
